/**
 * Messages from the service worker to the app (FE-04 §6.2): `push {tag, url}`, `navigate {url}`,
 * `outbox-flushed {sent}`. One listener on `navigator.serviceWorker`, fanned out to subscribers.
 */
export type SwMessage =
  | { type: "push"; tag?: string; url?: string }
  | { type: "navigate"; url: string }
  | { type: "outbox-flushed"; sent: number };

type Listener = (message: SwMessage) => void;

const listeners = new Set<Listener>();
let attached = false;

function isMessage(data: unknown): data is SwMessage {
  if (typeof data !== "object" || data === null) return false;
  const type = (data as { type?: unknown }).type;
  return type === "push" || type === "navigate" || type === "outbox-flushed";
}

/** Deliver a message to the subscribers (also used by tests and the BroadcastChannel fallback). */
export function dispatchSwMessage(message: SwMessage): void {
  for (const listener of [...listeners]) listener(message);
}

function attach(): void {
  const container = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
  if (attached || typeof container?.addEventListener !== "function") return;
  attached = true;
  container.addEventListener("message", (event: MessageEvent) => {
    if (isMessage(event.data)) dispatchSwMessage(event.data);
  });
}

export function onSwMessage(listener: Listener): () => void {
  attach();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Post a message to the active service worker (no-op without one). */
export function postToServiceWorker(message: { type: string } & Record<string, unknown>): void {
  navigator.serviceWorker?.controller?.postMessage(message);
}
